/* Hexdeck – global storefront behaviour (no dependencies). */
(() => {
  const H = window.Hexdeck || {};
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const debounce = (fn, ms = 250) => {
    let t;
    return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
  };

  /* ---------------------------------------------------------------- Toasts */
  const ICONS = {
    success: '<svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 5 5L20 7"/></svg>',
    error: '<svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16.5v.01"/></svg>'
  };
  function toast(message, type = 'success', link) {
    const region = $('[data-toast-region]');
    if (!region) return;
    const el = document.createElement('div');
    el.className = `toast toast--${type}`;
    el.innerHTML = ICONS[type] || '';
    const span = document.createElement('span');
    span.textContent = message;
    el.appendChild(span);
    if (link) {
      const a = document.createElement('a');
      a.href = link.href;
      a.textContent = link.label;
      el.appendChild(a);
    }
    region.appendChild(el);
    setTimeout(() => el.remove(), type === 'error' ? 6000 : 3500);
  }

  /* --------------------------------------------------------------- Dialogs */
  function openDialog(id, opener) {
    const dialog = document.getElementById(id);
    if (!dialog || dialog.open) return;
    dialog.showModal();
    document.body.classList.add('is-locked');
    if (opener) opener.setAttribute('aria-expanded', 'true');
    dialog.addEventListener('close', () => {
      document.body.classList.remove('is-locked');
      if (opener) {
        opener.setAttribute('aria-expanded', 'false');
        opener.focus({ preventScroll: true });
      }
    }, { once: true });
    const auto = dialog.querySelector('[autofocus], input[type="search"]');
    if (auto) requestAnimationFrame(() => auto.focus());
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-dialog]');
    if (opener) {
      e.preventDefault();
      openDialog(opener.dataset.openDialog, opener);
      return;
    }
    const closer = e.target.closest('[data-close-dialog]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }
    // Click on backdrop area (the dialog element itself) closes it.
    if (e.target.matches('dialog[data-dialog], dialog.modal')) e.target.close();
  });

  /* ------------------------------------------------------------- Mega menu */
  const megaItems = $$('[data-mega]');
  megaItems.forEach((item) => {
    const trigger = $('[data-mega-trigger]', item);
    let timer;
    const open = () => {
      clearTimeout(timer);
      megaItems.forEach((o) => o !== item && close(o));
      item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
    };
    const close = (el = item) => {
      el.classList.remove('is-open');
      $('[data-mega-trigger]', el)?.setAttribute('aria-expanded', 'false');
    };
    item.addEventListener('mouseenter', () => { timer = setTimeout(open, 70); });
    item.addEventListener('mouseleave', () => { clearTimeout(timer); timer = setTimeout(() => close(), 160); });
    item.addEventListener('focusin', open);
    item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) close(); });
    item.addEventListener('keydown', (e) => { if (e.key === 'Escape') { close(); trigger.focus(); } });
    // Touch: first tap opens the panel instead of navigating.
    trigger.addEventListener('click', (e) => {
      if (window.matchMedia('(hover: none)').matches && !item.classList.contains('is-open')) {
        e.preventDefault();
        open();
      }
    });
  });

  /* ----------------------------------------------------- Quantity steppers */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-minus], [data-qty-plus]');
    if (!btn) return;
    const wrap = btn.closest('[data-qty]');
    const input = wrap && $('input', wrap);
    if (!input) return;
    const min = parseInt(input.min || '0', 10);
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    const cur = parseInt(input.value || '0', 10) || 0;
    const next = btn.hasAttribute('data-qty-plus') ? Math.min(cur + 1, max) : Math.max(cur - 1, min);
    if (next === cur) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------------------ Cart */
  const Cart = {
    sectionIds() {
      const ids = [];
      if ($('[data-cart-drawer]')) ids.push('cart-drawer');
      const page = $('[data-cart-page]');
      if (page) ids.push(page.dataset.sectionId);
      return ids;
    },

    async request(url, body) {
      const sections = this.sectionIds();
      if (sections.length) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      let data = {};
      try { data = await res.json(); } catch (_) { /* ignore */ }
      if (!res.ok || data.status) {
        const err = new Error(data.description || data.message || H.strings.cartError);
        err.status = res.status;
        throw err;
      }
      if (data.sections) this.render(data.sections);
      return data;
    },

    render(sections) {
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (id === 'cart-drawer') {
          const fresh = $('[data-cart-drawer-inner]', doc);
          const current = $('[data-cart-drawer-inner]');
          if (fresh && current) {
            current.innerHTML = fresh.innerHTML;
            current.dataset.itemCount = fresh.dataset.itemCount;
          }
        } else {
          const fresh = $('[data-cart-page]', doc);
          const current = $('[data-cart-page]');
          if (fresh && current) {
            current.innerHTML = fresh.innerHTML;
            current.dataset.itemCount = fresh.dataset.itemCount;
          }
        }
      });
      this.updateCount();
    },

    async updateCount(count) {
      let n = count;
      if (n === undefined) {
        const holder = $('[data-cart-drawer-inner]') || $('[data-cart-page]');
        if (holder && holder.dataset.itemCount !== undefined) n = parseInt(holder.dataset.itemCount, 10);
        else {
          const c = await fetch(`${H.routes.cart}.js`).then((r) => r.json());
          n = c.item_count;
        }
      }
      $$('[data-cart-count]').forEach((el) => { el.textContent = n > 0 ? n : ''; });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: n } }));
    },

    add(items) {
      return this.request(`${H.routes.cartAdd}.js`, { items });
    },

    change(line, quantity) {
      return this.request(`${H.routes.cartChange}.js`, { line, quantity });
    },

    open() {
      if (H.cartType === 'page' || !$('#CartDrawer')) {
        if (!$('[data-cart-page]')) window.location.href = H.routes.cart;
        return;
      }
      openDialog('CartDrawer', $('[data-cart-toggle]'));
    }
  };
  H.cart = Cart;
  H.toast = toast;
  H.openDialog = openDialog;

  // Cart icon opens drawer
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-cart-toggle]');
    if (!t || !$('#CartDrawer') || H.cartType === 'page') return;
    e.preventDefault();
    openDialog('CartDrawer', t);
  });

  // Line quantity & remove
  const onLineChange = debounce(async (lineEl, qty) => {
    const errorEl = $('[data-line-error]', lineEl);
    lineEl.classList.add('is-updating');
    try {
      await Cart.change(parseInt(lineEl.dataset.index, 10), qty);
    } catch (err) {
      lineEl.classList.remove('is-updating');
      if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
      toast(err.message, 'error');
    }
  }, 350);

  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-qty]');
    if (!input) return;
    const line = input.closest('[data-cart-line]');
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    let qty = Math.max(0, parseInt(input.value, 10) || 0);
    if (qty > max) {
      qty = max;
      input.value = max;
      toast(H.strings.quantityError, 'error');
    }
    onLineChange(line, qty);
  });

  document.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-line-remove]');
    if (!rm) return;
    const line = rm.closest('[data-cart-line]');
    onLineChange(line, 0);
  });

  document.addEventListener('change', debounce((e) => {
    if (!e.target.matches('[data-cart-note]')) return;
    fetch(`${H.routes.cartUpdate}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: e.target.value })
    });
  }, 400));

  /* ------------------------------------------------------------ Quick add */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    const scope = btn.closest('[data-quick-scope]');
    const qtyInput = scope && $('[data-qty] input', scope);
    const item = { id: parseInt(btn.dataset.variantId, 10), quantity: qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1 };
    if (btn.dataset.preorder) item.properties = { 'Pre-order': btn.dataset.preorder };
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    try {
      await Cart.add([item]);
      if (H.cartType === 'drawer') Cart.open();
      else toast(H.strings.added, 'success', { href: H.routes.cart, label: 'Cart' });
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    }
  });

  /* --------------------------------------------------------- Product form */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = $('[type="submit"]', form);
    const fd = new FormData(form);
    const item = { id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity') || '1', 10) };
    const properties = {};
    for (const [k, v] of fd.entries()) {
      const m = k.match(/^properties\[(.+)\]$/);
      if (m && v) properties[m[1]] = v;
    }
    if (Object.keys(properties).length) item.properties = properties;
    btn?.classList.add('is-loading');
    const errorEl = $('[data-form-error]', form);
    if (errorEl) errorEl.hidden = true;
    try {
      await Cart.add([item]);
      if (H.cartType === 'drawer') Cart.open();
      else window.location.href = H.routes.cart;
    } catch (err) {
      if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
      toast(err.message, 'error');
    } finally {
      btn?.classList.remove('is-loading');
    }
  });

  /* ---------------------------------------------------- Predictive search */
  const searchForm = $('[data-predictive-search]');
  if (searchForm) {
    const input = $('input[type="search"]', searchForm);
    const results = $('[data-predictive-results]');
    const suggestions = $('[data-search-suggestions]');
    let controller;
    let activeIndex = -1;

    const setExpanded = (v) => input.setAttribute('aria-expanded', v ? 'true' : 'false');

    const search = debounce(async (q) => {
      if (controller) controller.abort();
      if (q.length < 2) {
        results.innerHTML = '';
        setExpanded(false);
        if (suggestions) suggestions.hidden = false;
        return;
      }
      controller = new AbortController();
      const params = new URLSearchParams({
        q,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,query',
        'resources[limit]': '8',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.sku,tag,vendor,variants.title'
      });
      try {
        const html = await fetch(`${H.routes.predictive}?${params}`, { signal: controller.signal }).then((r) => {
          if (!r.ok) throw new Error(r.status);
          return r.text();
        });
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = $('[data-predictive-content]', doc);
        results.innerHTML = content ? content.innerHTML : '';
        activeIndex = -1;
        setExpanded(true);
        if (suggestions) suggestions.hidden = true;
      } catch (err) {
        if (err.name !== 'AbortError') results.innerHTML = '';
      }
    }, 220);

    input.addEventListener('input', () => search(input.value.trim()));
    input.addEventListener('keydown', (e) => {
      const items = $$('[data-predictive-item]', results);
      if (!items.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = (activeIndex + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        items.forEach((el, i) => el.setAttribute('aria-selected', i === activeIndex ? 'true' : 'false'));
        items[activeIndex].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && activeIndex > -1) {
        e.preventDefault();
        window.location.href = items[activeIndex].href;
      }
    });
  }

  /* ----------------------------------------------------------- Lightbox */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-lightbox]');
    if (!trigger) return;
    e.preventDefault();
    let box = $('#Lightbox');
    if (!box) {
      box = document.createElement('dialog');
      box.id = 'Lightbox';
      box.className = 'modal lightbox';
      box.setAttribute('aria-label', trigger.getAttribute('aria-label') || 'Image');
      box.innerHTML = '<div class="modal__panel"><button type="button" class="btn btn--icon modal__close" data-close-dialog aria-label="Close"><svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg></button><img alt=""></div>';
      document.body.appendChild(box);
    }
    const img = $('img', box);
    img.src = trigger.dataset.lightbox;
    img.alt = trigger.dataset.alt || '';
    openDialog('Lightbox', trigger);
  });

  /* ------------------------------------------------------ Countdown timers */
  H.initCountdowns = (root = document) => $$('[data-countdown]', root).forEach((el) => {
    const target = new Date(el.dataset.countdown).getTime();
    if (Number.isNaN(target)) return;
    const parts = { d: $('[data-d]', el), h: $('[data-h]', el), m: $('[data-m]', el) };
    const tick = () => {
      const diff = Math.max(0, target - Date.now());
      if (parts.d) parts.d.textContent = Math.floor(diff / 864e5);
      if (parts.h) parts.h.textContent = String(Math.floor((diff % 864e5) / 36e5)).padStart(2, '0');
      if (parts.m) parts.m.textContent = String(Math.floor((diff % 36e5) / 6e4)).padStart(2, '0');
      if (diff === 0) { el.hidden = true; return; }
      setTimeout(tick, 30000);
    };
    tick();
  });
  H.initCountdowns();

  /* ------------------------------------------------------ Singles search
     Live results limited to Riftbound singles (name or card ID). Submitting
     opens the full search page restricted to singles. */
  $$('[data-singles-search]').forEach((form) => {
    const input = $('[data-singles-input]', form);
    const hidden = $('[data-singles-query]', form);
    const box = $('[data-singles-results]', form);
    let active = -1;
    let ctrl;
    const close = () => { box.hidden = true; input.setAttribute('aria-expanded', 'false'); active = -1; };
    const fmtPrice = (p) => {
      const cents = Math.round(parseFloat(p.price) * 100);
      if (!cents) return form.dataset.tba || 'Price coming soon';
      try { return new Intl.NumberFormat(document.documentElement.lang || 'nl', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR' }).format(cents / 100); } catch (_) { return p.price; }
    };
    const run = debounce(async () => {
      const term = input.value.trim();
      if (term.length < 2) { close(); box.innerHTML = ''; return; }
      if (ctrl) ctrl.abort();
      ctrl = new AbortController();
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product',
        'resources[limit]': '10',
        'resources[options][fields]': 'title,variants.sku,tag,product_type',
        'resources[options][unavailable_products]': 'last'
      });
      try {
        const data = await fetch(`${H.routes.predictive}.json?${params}`, { signal: ctrl.signal }).then((r) => r.json());
        const items = ((data.resources && data.resources.results && data.resources.results.products) || []).filter((p) => p.type === 'Single').slice(0, 8);
        if (!items.length) {
          box.innerHTML = `<p class="singles-search__empty">${form.dataset.empty || 'No singles found'}</p>`;
        } else {
          box.innerHTML = items.map((p) => {
            const img = p.featured_image && p.featured_image.url ? `${p.featured_image.url}${p.featured_image.url.includes('?') ? '&' : '?'}width=120` : '';
            const idMatch = p.title.match(/\(([A-Z]{2,4}-[^)]+)\)\s*$/);
            return `<a class="singles-search__item" href="${p.url}" role="option" aria-selected="false" data-singles-item>
              ${img ? `<img src="${img}" alt="" width="42" height="58" loading="lazy">` : '<span class="singles-search__noimg"></span>'}
              <span class="singles-search__meta"><strong></strong><span>${idMatch ? idMatch[1] : ''}</span></span>
              <span class="singles-search__price">${fmtPrice(p)}</span>
            </a>`;
          }).join('') + `<button type="submit" class="singles-search__all">${(form.dataset.all || 'View all results for "%s"').replace('%s', term.replace(/</g, '&lt;'))}</button>`;
          // Insert names as text (safe)
          $$('.singles-search__meta strong', box).forEach((el, i) => {
            const t = items[i].title.match(/\(([A-Z]{2,4}-[^)]+)\)\s*$/);
            el.textContent = t ? items[i].title.replace(t[0], '').trim() : items[i].title;
          });
        }
        box.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        active = -1;
      } catch (err) {
        if (err.name !== 'AbortError') close();
      }
    }, 200);
    input.addEventListener('input', run);
    input.addEventListener('focus', () => { if (box.innerHTML && input.value.trim().length > 1) { box.hidden = false; } });
    input.addEventListener('keydown', (e) => {
      const items = $$('[data-singles-item]', box);
      if (e.key === 'Escape') { close(); return; }
      if (!items.length || box.hidden) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        active = (active + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        items.forEach((el, i) => el.setAttribute('aria-selected', i === active ? 'true' : 'false'));
      } else if (e.key === 'Enter' && active > -1) {
        e.preventDefault();
        window.location.href = items[active].href;
      }
    });
    document.addEventListener('click', (e) => { if (!form.contains(e.target)) close(); });
    form.addEventListener('submit', (e) => {
      const term = input.value.trim();
      if (!term) { e.preventDefault(); return; }
      // Precise query: every word must match the title, card ID (SKU) or a tag (e.g. champion).
      const words = term.replace(/[():*"]/g, ' ').split(/\s+/).filter(Boolean);
      hidden.value = `product_type:Single AND ${words.map((w) => `(title:${w}* OR variants.sku:${w}* OR tag:${w})`).join(' AND ')}`;
    });
  });

  /* ------------------------------------------------ Riftbound card text icons
     Turns "[Accelerate]", "[3]", "[might]", "[Fury]", "[exhaust]" in card text
     into keyword labels and game symbols. Only text nodes are touched. */
  const RB_GLYPHS = {
    might: 'https://assetcdn.rgpub.io/public/live/riot-shared/player-experiences/riot-glyphs/rb/latest/might.svg',
    rune: 'https://cmsassets.rgpub.io/sanity/images/dsfx7636/game_data_live/fa3d8362379b722a5025995077a7fbd1b4a6ba0e-24x24.png',
    domains: {
      fury: 'https://cmsassets.rgpub.io/sanity/images/dsfx7636/game_data_live/5aeb4bfd203b5d265902f65aa5afae7da1682eaa-64x64.png',
      calm: 'https://cmsassets.rgpub.io/sanity/images/dsfx7636/game_data_live/b9ef2f5b74841ad11f3629aa381a76ac0187d007-64x64.png',
      mind: 'https://cmsassets.rgpub.io/sanity/images/dsfx7636/game_data_live/17ab95a6bd052085b6803d846a287f625f347288-64x64.png',
      body: 'https://cmsassets.rgpub.io/sanity/images/dsfx7636/game_data_live/7a5533034de5870808347bc4b296f0029bdd8eea-64x64.png',
      chaos: 'https://cmsassets.rgpub.io/sanity/images/dsfx7636/game_data_live/597ddb82be59e87b467c52bb10204f02c2005d06-64x64.png',
      order: 'https://cmsassets.rgpub.io/sanity/images/dsfx7636/game_data_live/8bb1b193a8e1adc26ca28e1a21da8d1e2f5d2f72-64x64.png'
    }
  };
  const RB_KEYWORD_GROUPS = {
    timing: ['action', 'reaction', 'hidden'],
    attack: ['assault', 'ganking', 'ambush', 'backline'],
    defense: ['shield', 'tank', 'deflect'],
    growth: ['empower', 'empowered', 'mighty', 'buff', 'level'],
    tempo: ['accelerate', 'legion', 'hunt', 'flow', 'repeat', 'predict', 'vision'],
    effect: ['stun', 'burn', 'deathknell', 'temporary', 'unique', 'equip', 'weaponmaster', 'add']
  };
  const kwGroup = (word) => {
    const w = word.toLowerCase();
    return Object.keys(RB_KEYWORD_GROUPS).find((g) => RB_KEYWORD_GROUPS[g].includes(w)) || 'other';
  };
  const EXHAUST_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.34-5.66" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/><path d="M20 4v5h-5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function rbToken(raw) {
    const t = raw.trim();
    const lower = t.toLowerCase();
    const span = document.createElement('span');
    if (/^\d+$/.test(t)) {
      span.className = 'rb-energy';
      span.textContent = t;
      span.title = `${t} Energy`;
      return span;
    }
    if (lower === 'might') {
      span.className = 'rb-sym rb-sym--might';
      span.title = 'Might';
      span.innerHTML = `<img src="${RB_GLYPHS.might}" alt="Might" width="14" height="14" loading="lazy">`;
      return span;
    }
    if (RB_GLYPHS.domains[lower] || lower === 'rainbow' || lower === 'any rune') {
      const src = RB_GLYPHS.domains[lower] || RB_GLYPHS.rune;
      const label = RB_GLYPHS.domains[lower] ? `${t} rune` : 'Any rune';
      span.className = `rb-sym rb-sym--rune rb-sym--${lower.replace(/\s+/g, '-')}`;
      span.title = label;
      span.innerHTML = `<img src="${src}" alt="${label}" width="16" height="16" loading="lazy">`;
      return span;
    }
    if (lower === 'exhaust') {
      span.className = 'rb-sym rb-sym--exhaust';
      span.title = 'Exhaust';
      span.innerHTML = EXHAUST_SVG;
      return span;
    }
    const word = t.split(/\s+/)[0];
    span.className = `rb-kw rb-kw--${kwGroup(word)}`;
    span.textContent = t;
    return span;
  }

  function renderCardText(root) {
    if (!root || root.dataset.rbRendered) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) if (/\[[^\]]+\]/.test(walker.currentNode.nodeValue)) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      const frag = document.createDocumentFragment();
      node.nodeValue.split(/(\[[^\]]+\])/).forEach((part) => {
        const m = part.match(/^\[([^\]]+)\]$/);
        frag.appendChild(m ? rbToken(m[1]) : document.createTextNode(part));
      });
      node.parentNode.replaceChild(frag, node);
    });
    root.dataset.rbRendered = '1';
  }
  H.cardText = { render: renderCardText };
  $$('[data-card-text]').forEach(renderCardText);

  /* ------------------------------------------ Lazy sections (recommendations) */
  const lazySections = $$('[data-lazy-section]');
  if (lazySections.length) {
    const load = async (el) => {
      try {
        const html = await fetch(el.dataset.lazySection).then((r) => (r.ok ? r.text() : ''));
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = $('[data-lazy-section-content]', doc);
        const hasFallback = !!$('[data-lazy-section-fallback]', el);
        if (fresh && fresh.children.length) el.innerHTML = fresh.outerHTML;
        else if (!hasFallback) el.remove();
      } catch (_) {
        if (!$('[data-lazy-section-fallback]', el)) el.remove();
      }
    };
    const io = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries, obs) => {
        entries.forEach((en) => { if (en.isIntersecting) { obs.unobserve(en.target); load(en.target); } });
      }, { rootMargin: '400px 0px' })
      : null;
    lazySections.forEach((el) => {
      if ($('[data-lazy-section-content] > *', el)) return;
      if (io) io.observe(el); else load(el);
    });
  }

  /* --------------------------------------------- Chip filters (events etc.) */
  $$('[data-chip-filter]').forEach((group) => {
    const target = document.getElementById(group.dataset.chipFilter);
    if (!target) return;
    group.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-filter-value]');
      if (!chip) return;
      $$('[data-filter-value]', group).forEach((c) => c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'));
      const v = chip.dataset.filterValue;
      let shown = 0;
      $$('[data-filter-item]', target).forEach((item) => {
        const match = v === 'all' || (item.dataset.filterItem || '').split(',').includes(v);
        item.hidden = !match;
        if (match) shown += 1;
      });
      const empty = document.querySelector(`[data-filter-empty="${group.dataset.chipFilter}"]`);
      if (empty) empty.hidden = shown > 0;
    });
  });
})();
